import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { NEW_ORDER_SESSION_STORAGE_KEY } from 'constants/ui/storageKey';
import { CreateNewOrderFormType } from 'api';

export type FormState<T> = {
  currentStep: number;
  totalStep: number;
  formData: T;
  setFormData: (data: T) => void;
  clearFormData: () => void;
  nextStep: () => void;
  goToStep: (step: number) => void;
  getCurrentStep: () => number;
  getState: () => FormState<T>;
  discardFlow: () => void;
};

type Props = {
  totalStep: number;
};

const useCreateNewOrderStore = <T>(props: Props) =>
  create(
    persist<FormState<T>>(
      (set, get) => ({
        currentStep: 1,
        totalStep: props.totalStep,
        formData: {} as T,
        setFormData: (data: T) => {
          set(state => {
            const newFormData = {
              ...state.formData,
              ...data,
            };
            return {
              ...state,
              formData: newFormData,
            };
          });
        },
        clearFormData: () => {
          set({
            currentStep: 1,
            formData: {} as T,
          });
          sessionStorage.removeItem(NEW_ORDER_SESSION_STORAGE_KEY);
        },
        nextStep: () => {
          set(state => ({
            ...state,
            currentStep: state.currentStep + 1,
          }));
        },
        goToStep: (step: number) => {
          set(state => ({
            ...state,
            currentStep: step,
          }));
        },
        getCurrentStep: () => get().currentStep,
        getState: () => get(),
        discardFlow: () => {
          set(state => ({
            ...state,
            currentStep: 1,
            formData: {} as T,
          }));
          sessionStorage.removeItem(NEW_ORDER_SESSION_STORAGE_KEY);
        },
      }),
      {
        name: NEW_ORDER_SESSION_STORAGE_KEY,
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  );

export const createNewOrderStore = useCreateNewOrderStore<CreateNewOrderFormType>({ totalStep: 5 });
